// NPM
import React, { useEffect, useState, FC } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { filter, indexOf, map, first } from "lodash";
import moment from "moment";

// STYLE
import "./version-accordion.scss";

// DATA
import tabColumnJSON from "../tab-columns.json";

// HELPERS
import { encode } from "Helpers/index";

// ANT COMPONENTS
import { BasicModal, Button } from "@life-line-lab/components-react";
import { Col, Row, Table, Collapse, Switch } from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import {
  CreateEditForm,
  AddCustomersForm,
  ApplicableCustomers,
  RestoreVersion,
} from "../modal-forms";
import { SettingTabColumns } from "../tab-columns";
import { OrderSettingsEnum } from "Data/settings";
import { getIndexOfOrderSettings } from "Helpers/order-settings-helper";

interface VersionAccordionProps {
  isCustomerPage?: any;
  versionData?: any;
  tabKey: OrderSettingsEnum;
  onAddCustomerSubmit: any;
  onSubmit: any;
  onUpdate: any;
  hideModal: any;
}

const CustomFormTabKey = [
  OrderSettingsEnum.RATE_TABLE,
  OrderSettingsEnum.INSURANCE,
  OrderSettingsEnum.CUSTOMER_RATE_TABLE,
];

const RateTableTabKeys = [OrderSettingsEnum.RATE_TABLE, OrderSettingsEnum.CUSTOMER_RATE_TABLE];

const PanelHeader: FC<any> = (props) => {
  const {
    versionData,
    tabKey,
    isCustomerPage,
    handleEditForm,
    handleEditFormCustomer,
    handleModalAction,
    t,
  } = props || {};

  return (
    <div className="va-panel-header">
      {/* if it is not a customer page, and tabkey is not customer rate table, show */}
      {!isCustomerPage && tabKey !== OrderSettingsEnum.CUSTOMER_RATE_TABLE && (
        <>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleModalAction("SHOW");
            }}
            type="link"
            label={t("action.showCustomer")}
          />
          <Button
            label={t("action.addCustomer")}
            onClick={(e) => {
              e.stopPropagation();
              handleModalAction("ADD");
            }}
          />
        </>
      )}
      {versionData?.active && (
        <Button
          label={t("action.edit")}
          onClick={(e) => {
            e.stopPropagation();

            const hasCustomForm = CustomFormTabKey.includes(tabKey) || versionData?.version == "1";

            if (hasCustomForm) {
              if (isCustomerPage) {
                handleEditFormCustomer(versionData);
                return;
              }

              handleEditForm(versionData);
              return;
            }

            handleModalAction("EDIT");
          }}
          type="primary"
        />
      )}
    </div>
  );
};

const AddNewVersionButton: FC<any> = (props) => {
  const { versionData, tabKey, handleCreateForm, handleModalAction, t } = props || {};

  return (
    <div className="va-adv-button-container">
      {versionData.active ? (
        <Button
          label={t("action.createNewVersion")}
          type="primary"
          onClick={() => {
            CustomFormTabKey.includes(tabKey) ? handleCreateForm() : handleModalAction("CREATE");
          }}
        />
      ) : (
        <Button
          label={t("action.restoreVersion")}
          type="primary"
          onClick={() => {
            handleModalAction("RESTORE");
          }}
        />
      )}
    </div>
  );
};

const VersionAccordion: React.FC<VersionAccordionProps> = ({
  isCustomerPage,
  versionData,
  tabKey,
  onAddCustomerSubmit,
  onSubmit,
  onUpdate,
  hideModal,
}) => {
  const history = useHistory();
  const { Panel } = Collapse;
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const initialModal: any = {
    open: false,
    type: "",
  };
  const [modal, setModal] = useState<any>(initialModal);
  const [activeColumns, setActiveColumns] = useState<any>([]);

  const allPossibleColumns = [
    {
      title: "MAX WGT (KG)",
      dataIndex: "maxWeight",
      key: "maxWeight",
    },
    {
      title: "MAX DIMENSION",
      dataIndex: "maxDimension",
      key: "maxDimension",
      render: (_: any, record: any) => {
        return (
          <React.Fragment>
            <span>
              {record?.maxDimension?.length} x {record?.maxDimension?.width} x{" "}
              {record?.maxDimension?.height}
            </span>
          </React.Fragment>
        );
      },
    },
    {
      title: "SURCHARGE",
      dataIndex: "surcharge",
      key: "surcharge",
      render: (_: any, record: any) => {
        return (
          <div className="surcharge-display">
            {record?.surcharge?.fixedAmount
              ? `RM ${record.surcharge.fixedAmount} ${
                  record?.surcharge?.percentage ? `OR ${record.surcharge.percentage}%` : ""
                }`
              : `${record.surcharge.percentage}%`}
          </div>
        );
      },
    },
    {
      title: "CHARGE",
      dataIndex: "insuranceCharges",
      key: "charge",
      render: (_: any, record: any) => {
        return (
          <React.Fragment>
            {map(record?.insuranceCharges, (row, i) => {
              return (
                <span key={i}>
                  {row.charge}
                  <br />
                </span>
              );
            })}
          </React.Fragment>
        );
      },
    },
    {
      title: "VALID FROM",
      dataIndex: "validFrom",
      key: "validFrom",
      render: (text: any, record: any) => {
        if (!text) return;

        return <span>{moment(text).format("DD-MM-YYYY") || null}</span>;
      },
    },
    {
      title: "VALID TO",
      dataIndex: "validTo",
      key: "validTo",
      render: (text: any, record: any) => {
        if (!text) return;

        return <span>{moment(text).format("DD-MM-YYYY") || null}</span>;
      },
    },
  ];

  const getDataTableColumns = (tab) => {
    let currentTabColumns = SettingTabColumns[tab].columns;

    const filterColumns = filter(allPossibleColumns, (colObj) => {
      return indexOf(currentTabColumns, colObj.key) != -1;
    });
    setActiveColumns(filterColumns);
  };

  useEffect(() => {
    getDataTableColumns(tabKey);
  }, [tabKey]);

  useEffect(() => {
    handleResetModalState();
  }, [hideModal]);

  const handleModalAction = (type) => {
    switch (type) {
      case "CREATE": {
        setModal({ open: true, type: "create" });
        break;
      }
      case "EDIT": {
        setModal({ open: true, type: "edit" });
        break;
      }
      case "ADD": {
        setModal({ open: true, type: "add" });
        break;
      }
      case "SHOW": {
        setModal({ open: true, type: "show" });
        break;
      }
      case "RESTORE": {
        setModal({ open: true, type: "restore" });
        break;
      }
    }
  };

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const handleEditForm = (versionData) => {
    // redirects to stack screen for edits
    let encodedID = encode(versionData._id);
    history.push(
      `/order-management/settings/form/${getIndexOfOrderSettings(tabKey)}/${encodedID}/edit`,
    );
  };

  const handleEditFormCustomer = (versionData) => {
    let encodedID = encode(versionData._id);
    history.push(
      `/order-management/customer/${id}/settings/form/${getIndexOfOrderSettings(
        tabKey,
      )}/${encodedID}/edit`,
    );
  };

  const handleCreateForm = () => {
    // redirects to stack screen for creating version
    let defaultStr = `/order-management/settings/form/${getIndexOfOrderSettings(
      tabKey,
    )}?version-create=true&`;

    if (RateTableTabKeys.includes(tabKey)) {
      if (versionData.customerOrgID) {
        // attach customer org id
        const customerID = encode(versionData.customerOrgID);
        defaultStr += `cOrgID=${customerID}&`;
      }

      // attach location org id
      const firstLocation: any = first(versionData.locations);

      if (firstLocation) {
        const encodedCode = encode(firstLocation.code);
        defaultStr += `locCode=${encodedCode}&`;
      }
    }

    const encodedCode = encode(versionData.code);
    defaultStr += `code=${encodedCode}`;

    history.push(defaultStr);
  };

  const addNewVersionButtonProps = {
    versionData,
    tabKey,
    isCustomerPage,
    handleCreateForm,
    handleModalAction,
    t,
  };

  const panelHeaderProps = {
    versionData,
    tabKey,
    isCustomerPage,
    handleEditForm,
    handleEditFormCustomer,
    handleModalAction,
    t,
  };

  return (
    <div className="version-accordion-container">
      {!isCustomerPage && (
        <div className="va-header">
          <AddNewVersionButton {...addNewVersionButtonProps} />
        </div>
      )}
      <Collapse
        bordered={false}
        defaultActiveKey={versionData?.active ? versionData?.version : "0"}
        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
      >
        <Panel
          key={versionData?.version}
          header={<span className="va-panel-title">Version {versionData?.version}</span>}
          extra={<PanelHeader {...panelHeaderProps} />}
        >
          <Table
            rowKey={(rowKeyData: any) => {
              return rowKeyData?.name || rowKeyData?.areaName;
            }}
            columns={activeColumns}
            dataSource={
              [versionData.data].includes(undefined) ? [versionData] : [versionData?.data]
            }
            pagination={false}
          />
        </Panel>
      </Collapse>
      <BasicModal
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={handleResetModalState}
        width={modal.type == "add" ? "50vw" : "520px"}
      >
        {modal.type == "create" || modal.type == "edit" ? (
          <CreateEditForm
            type={modal.type}
            info={versionData}
            tab={getIndexOfOrderSettings(tabKey)}
            onCancel={handleResetModalState}
            onSubmit={onSubmit}
            onUpdate={onUpdate}
          />
        ) : modal.type == "add" ? (
          <AddCustomersForm
            versionInfo={versionData}
            onSubmit={onAddCustomerSubmit}
            handleCancel={handleResetModalState}
          />
        ) : modal.type == "show" ? (
          <ApplicableCustomers info={versionData} />
        ) : modal.type == "restore" ? (
          <RestoreVersion
            info={versionData}
            onSubmit={onSubmit}
            onCancel={handleResetModalState}
            tabKey={tabKey}
          />
        ) : (
          <></>
        )}
      </BasicModal>
    </div>
  );
};

export default VersionAccordion;
